<template>
    <div :class="formClass" v-click-outside="closeDropdown">
        <label class="form__label" v-if="label" :for="name">{{ label }}</label>

        <div class="form__input pt-0">
            <div :class="{'dropdown-wrap': true, 'loading' : loading, 'loading--dropdown' : loading, 'dropdown-wrap--multiple': true, 'is-open': dropdownOpen }">
                <i class="choices__chevron fi-angle-down" @click="toggleDropdown"></i>

                <div class="choices__inner" tabindex="0" @click="openDropdown" @focus="openDropdown">
                    <select tabindex="-1" ref="input" :name="name" class="dropdown__select--vue" style="height: 1px; opacity: 0; position: absolute; width: 1px;"
                            multiple="multiple" @change="changeInnerSelect" @click.stop.prevent="closeDropdown">
                        <option v-for="item in parsedOptions" :value="item.value"
                                :selected="innerSelect.includes(item.value)">
                            {{ item.text }}
                        </option>
                    </select>

                    <div>
                        <div class="choices__card" v-for="item in innerSelect"
                             @click.stop="removeSelectedItem(item)">
                            {{ getOptionLabel(item) }}
                        </div>
                    </div>
                    <input type="text" class="choices__input" autocomplete="off"
                           ref:filterboxmulti
                           tabindex="-1"
                           :value="filter"
                           @keydown="preventEnter"
                           @keyup="updateFilter"
                           @change.stop="preventEnter"
                           :placeholder="currentPlaceHolder"
                    >
                </div>
                <div class="dropdown-list" :class="{ 'is-active': dropdownOpen }" aria-expanded="false">
                    <div class="dropdown-list__options" role="listbox">
                        <div class="dropdown-list__option"
                             role="option"
                             v-for="item in visibleOptions"
                             :class="{'is-highlighted': innerSelect.includes(item.value)}"
                             @click.stop="selectItem(item)"
                             :data-value="item.value">
                            {{ item.text }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="buffer buffer--large">
            </div>
        </div>

        <div v-if="errorList.length" class="form__error">
            <span v-for="error in errorList">{{ error }}</span>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue';
import vClickOutside from 'click-outside-vue3';

export default defineComponent({
    props: {
        'modelValue': {type: Array as PropType<string[]>, default: () => []},
        'name': {type: String, required: true},
        'label': {type: String, default: ''},
        'placeholder': {type: String, default: ''},
        'options': {type: Array as PropType<DropdownOptions>},
        'searchable': {type: Boolean, default: false},
        'laravelErrors': {type: Array as PropType<ValidationErrors>, default: () => []},
        'validationErrors': {type: Array as PropType<ValidationErrors>, default: () => []},
        'small': {type: Boolean, default: false},
        'loading': {type: Boolean, default: false},
        /** When `1`, only one value is kept; picking another replaces it (single-select UX). Omit for unlimited multi-select. */
        'maxSelections': {type: Number as PropType<number | undefined>, default: undefined},
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    emits: [
        'update:modelValue',
        'change',
    ],
    data() {
        return {
            innerSelect: [] as string[],
            dropdownOpen: false,
            filter: '',
        };
    },

    computed: {
        currentPlaceHolder(): string {
            if (this.dropdownOpen) {
                return 'Filter list';
            }

            return this.placeholder;
        },
        errorList(): ValidationErrors {
            return this.validationErrors || this.laravelErrors || [];
        },
        formClass(): VueClassMapping {
            return {
                'form__field': true,
                'form__field--dropdown': true,
                'form__field--small': this.small,
                'form__field--invalid': this.errorList.length > 0,
                'loading': this.loading,
            };
        },
        visibleOptions(): DropdownOptions {
            let visibleOptions = this.parsedOptions;

            // filter out selected items in multiselect (unless single-select mode, where picking another option replaces the current one)
            const hideSelected = this.maxSelections !== 1;
            if (hideSelected) {
                visibleOptions = visibleOptions.filter((item) => !this.innerSelect.includes(item.value) && item.value.toString() !== '');
            } else {
                visibleOptions = visibleOptions.filter((item) => item.value.toString() !== '');
            }

            // search items
            if (this.searchable && this.filter !== '') {
                visibleOptions = visibleOptions.filter((item) => item.text.toLowerCase().indexOf(this.filter.toLowerCase()) !== -1);
            }

            return visibleOptions;
        },
        parsedOptions(): DropdownOptions {
            const parsed = [] as DropdownOptions;

            if (Array.isArray(this.options)) {
                this.options.forEach((item) => {
                    // filter out default value from multiple select as used in placeholder
                    if (item.value.toString() === '') {
                        return;
                    }

                    // convert everything to strings
                    parsed.push({
                        text: item.text.toString(),
                        value: item.value.toString(),
                    });
                });
            }

            return parsed;
        },
    },
    watch: {
        modelValue() {
            this.detectDefaultValue();
        },
    },
    methods: {
        preventEnter(e: KeyboardEvent) {
            if (e.code === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
            }
        },
        updateFilter(e: KeyboardEvent) {
            const target = e.target as HTMLInputElement;

            this.filter = target.value;
        },
        toggleDropdown() {
            if (this.dropdownOpen) {
                this.closeDropdown();
            } else {
                this.openDropdown();
            }
        },
        openDropdown() {
            this.dropdownOpen = true;

            if (this.searchable) {
                this.focusSearchBox();
            }
        },
        closeDropdown() {
            this.dropdownOpen = false;
            this.filter = '';
        },
        focusSearchBox() {
            const filterInput = this.$refs.filterboxmulti as HTMLInputElement;
            if (filterInput) {
                filterInput.focus();
            }
        },
        addSelectedItem(item:string, emit:boolean) {
            this.cleanupSelected();
            if (this.maxSelections === 1) {
                this.innerSelect = [item];
            } else if (!this.innerSelect.includes(item)) {
                this.innerSelect.push(item);
            }
            if (emit) {
                this.syncValuesToParent();
            }
        },
        cleanupSelected() {
            const parsedValues = this.parsedOptions.map((item) => item.value);
            this.innerSelect = this.innerSelect.filter((item) => parsedValues.includes(item));
        },
        removeSelectedItem(item: string) {
            if (!this.innerSelect.includes(item)) {
                return;
            }
            this.innerSelect = this.innerSelect.filter((el) => el !== item);
            this.syncValuesToParent();
        },
        selectItem(item: DropdownOption) {
            this.filter = '';

            this.closeDropdown();
            this.addSelectedItem(item.value, true);
        },
        syncValuesToParent() {
            const value = this.innerSelect;

            this.$emit('update:modelValue', value);
            this.$emit('change', value);
        },
        getOptionLabel(value: string) {
            const option = this.parsedOptions.find((option) => option.value === value);
            if (option === undefined) {
                return '';
            }

            return option.text;
        },
        isValidValue(value: string):boolean {
            if (value === '') {
                return false;
            }

            const option = this.parsedOptions.find((option) => option.value === value.toString());

            return option !== undefined;
        },
        detectDefaultValue() {
            this.innerSelect = [];
            this.modelValue.forEach((selectedValue) => {
                if (this.isValidValue(selectedValue)) {
                    this.addSelectedItem(selectedValue, false);
                }
            });
        },
        changeInnerSelect(e: any) {
            this.innerSelect = [];
            for (const item of e.target.children) {
                if (item.selected) {
                    this.selectItem(item);
                }
            }
            e.stopPropagation();
            this.closeDropdown();
        },
    },
    mounted() {
        this.detectDefaultValue();
    },
});
</script>
